import dayjs from 'dayjs'
import {
    Activity,
    AlertTriangle,
    Calendar,
    CheckCircle2,
    ExternalLink,
    Shield,
    TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import Paper from '@/components/Paper'
import { Button } from '@/components/ui/button'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getServicesByCustomerId } from '@/modules/Customer/services'
import { GetListService } from '@/modules/Customer/types'
import {
    CustomerUptimeStats,
    getCustomerUptimeStats,
    getServiceSLA,
} from '@/modules/Service/services/sla'
import { ServiceSLA, SLATier } from '@/modules/Service/types/sla'
import CustomerDowntimeLogs from './CustomerDowntimeLogs'
import CustomerTicketsList from './CustomerTicketsList'

// Period options
const PERIOD_OPTIONS = [
    { value: 'this_month', label: 'Tháng này' },
    { value: 'last_month', label: 'Tháng trước' },
    { value: 'last_3_months', label: '3 tháng' },
]

const getPeriodDates = (period: string) => {
    const now = dayjs()
    switch (period) {
        case 'this_month':
            return {
                start: now.startOf('month').format('YYYY-MM-DD'),
                end: now.endOf('month').format('YYYY-MM-DD'),
            }
        case 'last_month':
            return {
                start: now
                    .subtract(1, 'month')
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                end: now
                    .subtract(1, 'month')
                    .endOf('month')
                    .format('YYYY-MM-DD'),
            }
        case 'last_3_months':
            return {
                start: now
                    .subtract(3, 'month')
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                end: now.endOf('month').format('YYYY-MM-DD'),
            }
        default:
            return {
                start: now.startOf('month').format('YYYY-MM-DD'),
                end: now.endOf('month').format('YYYY-MM-DD'),
            }
    }
}

// Format minutes to duration string
const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

// Format minutes to readable string
const formatMinutes = (minutes: number | undefined) => {
    if (!minutes) return '-'
    if (minutes < 60) return `${minutes} phút`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} giờ`
}

// SLA Info Display Component - Gray theme
const SLAInfoDisplay: React.FC<{ sla: ServiceSLA }> = ({ sla }) => {
    const tier = sla?.sla_template?.template_tier as SLATier | undefined

    // Get effective values (from SLA or template)
    const uptime = sla.uptime_guarantee || 99
    const responseTime = sla.sla_template?.default_response_time || 0
    const resolutionTime = sla.sla_template?.default_resolution_time || 0

    const rulesCount = sla.sla_rules?.length || 0
    const escalationCount = sla.escalation_rules?.length || 0
    const termsCount = sla.custom_terms?.length || 0
    const is24x7 =
        sla.business_hours_config?.include_weekends || !sla.business_hours_only

    return (
        <div className="p-3 bg-gray-50 rounded-lg text-xs">
            {/* Header: Tier + Status */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-gray-500" />
                    <span className="font-medium text-gray-700">
                        {tier?.toUpperCase() || 'Custom'}
                    </span>
                </div>
                <span
                    className={`text-xs ${sla.is_active ? 'text-gray-600' : 'text-gray-400'}`}
                >
                    {sla.is_active ? 'Active' : 'Inactive'}
                </span>
            </div>

            {/* Metrics - Compact */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-gray-600">
                <span>
                    Uptime: <strong className="text-gray-700">{uptime}%</strong>
                </span>
                <span>
                    Phản hồi:{' '}
                    <strong className="text-gray-700">
                        {formatMinutes(responseTime)}
                    </strong>
                </span>
                <span>
                    Xử lý:{' '}
                    <strong className="text-gray-700">
                        {formatMinutes(resolutionTime)}
                    </strong>
                </span>
                <span>{is24x7 ? '24/7' : 'Giờ HC'}</span>
            </div>

            {/* Additional counts */}
            {(rulesCount > 0 || escalationCount > 0 || termsCount > 0) && (
                <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200 text-gray-500">
                    {rulesCount > 0 && <span>{rulesCount} rules</span>}
                    {escalationCount > 0 && (
                        <span>{escalationCount} cảnh báo</span>
                    )}
                    {termsCount > 0 && <span>{termsCount} điều khoản</span>}
                </div>
            )}
        </div>
    )
}

// Service card with SLA info - Gray theme
const ServiceSLACard: React.FC<{
    service: GetListService
    onViewSLA: (serviceId: number) => void
}> = ({ service, onViewSLA }) => {
    const { t } = useTranslation('services')

    // Fetch SLA for this service
    const { data: slaData, isLoading: isLoadingSLA } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, service.service_id, 'sla'],
        queryFn: () => getServiceSLA(service.service_id),
    })

    const sla = slaData?.result
    const hasSLA = !!sla
    const isActive = service.service_status === 'active'

    return (
        <Card className="transition-all hover:shadow-sm">
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-medium text-gray-700 truncate">
                            {service.service_name}
                        </CardTitle>
                        <CardDescription className="text-xs text-gray-400">
                            ID: {service.service_id}
                        </CardDescription>
                    </div>
                    <span
                        className={`text-xs px-2 py-0.5 rounded ${isActive ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-400'}`}
                    >
                        {service.service_status}
                    </span>
                </div>
            </CardHeader>
            <CardContent className="space-y-2">
                {/* Product Group */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Activity className="w-3.5 h-3.5" />
                    <span className="truncate">
                        {service.product_group_name}
                    </span>
                </div>

                {/* SLA Info */}
                {isLoadingSLA ? (
                    <Skeleton className="h-16 w-full rounded-lg" />
                ) : hasSLA ? (
                    <SLAInfoDisplay sla={sla} />
                ) : (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs text-gray-500">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span>
                            {t('sla.no_sla_configured', 'Chưa cấu hình SLA')}
                        </span>
                    </div>
                )}

                {/* View SLA Button */}
                <Button
                    variant="ghost"
                    size="sm"
                    className="w-full h-8 text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => onViewSLA(service.service_id)}
                >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    {hasSLA ? 'Xem SLA' : 'Cấu hình SLA'}
                </Button>
            </CardContent>
        </Card>
    )
}

// Loading skeleton for cards
const CardSkeleton = () => (
    <Card>
        <CardHeader className="pb-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-3 w-1/4" />
        </CardHeader>
        <CardContent className="space-y-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-9 w-full" />
        </CardContent>
    </Card>
)

// Customer Uptime Overview Component
const CustomerUptimeOverview: React.FC<{
    customerId: number
    period: string
    onPeriodChange: (period: string) => void
}> = ({ customerId, period, onPeriodChange }) => {
    const { t } = useTranslation('services')
    const { start, end } = getPeriodDates(period)

    // Fetch customer uptime stats
    const { data: statsData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SLA_CHANGES, customerId, 'uptime-stats'],
        queryFn: () =>
            getCustomerUptimeStats(customerId, {
                start_date: start,
                end_date: end,
            }),
        enabled: !!customerId,
    })

    const stats: CustomerUptimeStats | undefined = statsData?.result

    // Calculate compliance percentage
    // const compliancePercent = useMemo(() => {
    //     if (!stats || stats.total_services === 0) return 0
    //     return Math.round(
    //         (stats.services_meeting_sla / stats.total_services) * 100
    //     )
    // }, [stats])

    if (isLoading) {
        return (
            <Paper className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-9 w-32" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-20" />
                    ))}
                </div>
            </Paper>
        )
    }

    // Show empty state if no services with SLA
    if (!stats || stats.total_services === 0) {
        return (
            <Paper className="p-4">
                <div className="flex items-center gap-2 text-gray-500">
                    <TrendingUp className="w-5 h-5" />
                    <span className="text-sm">
                        {t(
                            'sla.no_active_sla_services',
                            'Chưa có dịch vụ nào có SLA đang hoạt động'
                        )}
                    </span>
                </div>
            </Paper>
        )
    }

    return (
        <Paper className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-gray-500" />
                    <h3 className="font-medium text-gray-700 text-sm">
                        {t('sla.uptime_overview', 'Tổng quan Uptime')}
                    </h3>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>
                            {dayjs(start).format('DD/MM')} -{' '}
                            {dayjs(end).format('DD/MM/YYYY')}
                        </span>
                    </div>
                    <Select value={period} onValueChange={onPeriodChange}>
                        <SelectTrigger className="w-24 h-7 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIOD_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stats - Compact inline */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">SLA:</span>
                    <span className="font-semibold text-gray-700">
                        {stats.total_services}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">Uptime:</span>
                    <span
                        className={`font-semibold ${stats.avg_uptime_percent >= 99.5 ? 'text-green-600' : stats.avg_uptime_percent >= 99 ? 'text-gray-700' : 'text-red-600'}`}
                    >
                        {stats.avg_uptime_percent.toFixed(2)}%
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">Đạt:</span>
                    <span className="font-semibold text-gray-700">
                        {stats.services_meeting_sla}/{stats.total_services}
                    </span>
                </div>
                {stats.services_breaching_sla > 0 && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Vi phạm:</span>
                        <span className="font-semibold text-red-600">
                            {stats.services_breaching_sla}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">Downtime:</span>
                    <span className="font-semibold text-gray-700">
                        {formatDuration(stats.total_sla_downtime_minutes)}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">Sự cố:</span>
                    <span className="font-semibold text-gray-700">
                        {stats.total_incidents}
                    </span>
                </div>
            </div>
        </Paper>
    )
}

const ManageSLA: React.FC = () => {
    const { id } = useParams()
    const { t } = useTranslation('services')
    const navigate = useNavigate()
    const [period, setPeriod] = useState('this_month')

    // Fetch services
    const { data: servicesData, isLoading: isLoadingServices } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SERVICES, id, 'sla-management'],
            queryFn: () =>
                getServicesByCustomerId({
                    customer_id: Number(id),
                    is_poc: false,
                    page: 1,
                    page_size: 100,
                    q: '',
                }),
            enabled: !!id,
        })

    const services = servicesData?.results || []

    const handleViewServiceSLA = (serviceId: number) => {
        navigate(`/services/${serviceId}?tab=sla`)
    }

    // Count active services
    const activeCount = services.filter(
        (s) => s.service_status === 'active'
    ).length

    if (isLoadingServices) {
        return (
            <div className="space-y-4 mt-2">
                <Paper className="p-4">
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-lg" />
                        <div>
                            <Skeleton className="h-4 w-24 mb-1" />
                            <Skeleton className="h-8 w-12" />
                        </div>
                    </div>
                </Paper>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <CardSkeleton key={i} />
                    ))}
                </div>
            </div>
        )
    }

    // Get period dates for downtime logs
    const { start: periodStart, end: periodEnd } = getPeriodDates(period)

    return (
        <div className="space-y-2 mt-2">
            {/* Customer Uptime Overview */}
            <CustomerUptimeOverview
                customerId={Number(id)}
                period={period}
                onPeriodChange={setPeriod}
            />

            {/* Customer Downtime Logs */}
            <CustomerDowntimeLogs
                customerId={Number(id)}
                startDate={periodStart}
                endDate={periodEnd}
                services={services.map((s) => ({
                    service_id: s.service_id,
                    service_name: s.service_name,
                }))}
            />

            {/* Customer Tickets */}
            <CustomerTicketsList
                customerId={Number(id)}
                startDate={periodStart}
                endDate={periodEnd}
            />

            {/* Summary Stats - Inline compact */}
            <Paper className="p-4">
                <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                        <Shield className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-500">
                            {t('sla.total_services', 'Tổng')}:
                        </span>
                        <span className="font-semibold text-gray-700">
                            {services.length}
                        </span>
                    </div>
                    <div className="h-4 w-px bg-gray-200" />
                    <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-gray-500">
                            {t('sla.active_services', 'Active')}:
                        </span>
                        <span className="font-semibold text-green-600">
                            {activeCount}
                        </span>
                    </div>
                </div>
            </Paper>

            {/* Services Grid */}
            {services.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {services.map((service) => (
                        <ServiceSLACard
                            key={service.service_id}
                            service={service}
                            onViewSLA={handleViewServiceSLA}
                        />
                    ))}
                </div>
            ) : (
                <Paper className="p-8">
                    <div className="text-center">
                        <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                            {t('sla.no_services', 'Chưa có dịch vụ')}
                        </h3>
                        <p className="text-gray-500">
                            {t(
                                'sla.no_services_description',
                                'Khách hàng chưa có dịch vụ nào được thiết lập'
                            )}
                        </p>
                    </div>
                </Paper>
            )}
        </div>
    )
}

export default ManageSLA
